import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Avatar,
  Chip,
  Divider,
  Button,
  Stack,
  TextField,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Cake as CakeIcon,
  Favorite as HeartIcon,
  CalendarToday as CalendarIcon,
  OpenInNew as OpenInNewIcon,
  AutoAwesome as SparkleIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import moment from "moment";
import { capitalizeWords } from "functions";

export default function CelebrationDetailsDialog({
  open,
  onClose,
  data,
  type = "Birthday", // "Birthday" | "Anniversary"
}) {
  const theme = useTheme();
  const isBirthday = type === "Birthday";

  // Dynamic colors
  const primaryColor = isBirthday ? "#F59E0B" : "#EC4899";
  const primaryDark = isBirthday ? "#D97706" : "#BE185D";

  const isToday = data?.DateCategory === "Today";
  const isTomorrow = data?.DateCategory === "Tomorrow";

  const defaultMsg = isBirthday
    ? `Happy Birthday ${capitalizeWords((data?.FamilyMember || "").split(" ")[0])}! 🎂🎉 Wishing you a wonderful year ahead filled with joy, happiness, and good health!`
    : `Happy Anniversary ${capitalizeWords((data?.FamilyMember || "").split(" ")[0])}! 💑🎉 Wishing you both many more wonderful years of togetherness and happiness!`;

  const [customMsg, setCustomMsg] = useState(defaultMsg);

  // Update default message when data changes
  React.useEffect(() => {
    if (data) {
      const name = capitalizeWords((data?.FamilyMemberName || "").split(" ")[0]);
      setCustomMsg(
        isBirthday
          ? `Happy Birthday ${name}! 🎂🎉 Wishing you a wonderful year ahead filled with joy, happiness, and good health!`
          : `Happy Anniversary ${name}! 💑🎉 Wishing you both many more wonderful years of togetherness and happiness!`
      );
    }
  }, [data, isBirthday]);

  if (!data) return null;

  const pictureSrc = data.Picture
    ? data.Picture.startsWith("http")
      ? data.Picture
      : `https://backend.dahiyainsurance.com/Upload/profile/${data.Picture}`
    : null;

  const handleSendWhatsApp = () => {
    if (!data?.Mobile) return;
    const encoded = encodeURIComponent(customMsg);
    window.open(`https://wa.me/91${data.Mobile}?text=${encoded}`, "_blank");
  };

  const handleCall = () => {
    if (!data?.Mobile) return;
    window.open(`tel:${data.Mobile}`);
  };

  const fullViewUrl =
    data?.Type === "Agent"
      ? `/CLIAProspect/EditCLIAProspect/${data?.MainMemberID}`
      : `/Prospects/ViewProspect/${data?.MainMemberID}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.95)
              : "#ffffff",
          backdropFilter: "blur(20px)",
          boxShadow: `0 24px 48px ${alpha(primaryColor, 0.2)}`,
        },
      }}
    >
      {/* ── Header Banner ── */}
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${alpha(primaryColor, 0.15)} 0%, ${alpha(
            primaryDark,
            0.08
          )} 100%)`,
          borderBottom: `1px solid ${alpha(primaryColor, 0.15)}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})`,
              boxShadow: `0 4px 14px ${alpha(primaryColor, 0.4)}`,
            }}
          >
            {isBirthday ? (
              <CakeIcon sx={{ color: "#fff", fontSize: 22 }} />
            ) : (
              <HeartIcon sx={{ color: "#fff", fontSize: 22 }} />
            )}
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
              {isBirthday ? "Birthday Celebration" : "Anniversary Celebration"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isToday ? "Happening Today 🎉" : isTomorrow ? "Coming up Tomorrow ⏳" : data?.DateCategory}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.5}>
          <Tooltip title="View Complete Profile" arrow>
            <IconButton
              component="a"
              href={fullViewUrl}
              target="_blank"
              size="small"
              sx={{ color: primaryDark }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* ── Dialog Content ── */}
      <DialogContent sx={{ p: 3 }}>
        {/* Profile Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 3 }}>
          <Avatar
            src={pictureSrc || undefined}
            sx={{
              width: 72,
              height: 72,
              fontSize: "1.8rem",
              fontWeight: 700,
              bgcolor: alpha(primaryColor, 0.15),
              color: primaryDark,
              border: `3px solid ${primaryColor}`,
              boxShadow: `0 6px 18px ${alpha(primaryColor, 0.3)}`,
            }}
          >
            {data.FamilyMember?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} noWrap sx={{ textTransform: "capitalize" }}>
              {capitalizeWords(data.FamilyMember)}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: "wrap", gap: 0.5 }}>
              {isToday && (
                <Chip
                  icon={<SparkleIcon sx={{ fontSize: "14px !important", color: "#fff !important" }} />}
                  label="TODAY!"
                  size="small"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.68rem",
                    bgcolor: primaryColor,
                    color: "#fff",
                  }}
                />
              )}
              {isTomorrow && (
                <Chip
                  label="TOMORROW"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    bgcolor: alpha(primaryColor, 0.12),
                    color: primaryDark,
                    border: `1px solid ${alpha(primaryColor, 0.25)}`,
                  }}
                />
              )}
              {data.YearsCompleted && (
                <Chip
                  label={
                    isBirthday
                      ? `${data.YearsCompleted} Years Completed`
                      : `${data.YearsCompleted}th Year Together`
                  }
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                  }}
                />
              )}
              {data.Type && (
                <Chip
                  label={data.Type}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem" }}
                />
              )}
            </Stack>
          </Box>
        </Box>

        <Divider sx={{ mb: 2.5, opacity: 0.5 }} />

        {/* Info Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            mb: 3,
            p: 2,
            borderRadius: 2.5,
            bgcolor: alpha(theme.palette.action.hover, 0.3),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.3 }}>
              Mobile Number
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {data.Mobile || "Not provided"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.3 }}>
              {isBirthday ? "Birth Date (Celebration)" : "Anniversary Date"}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <CalendarIcon sx={{ fontSize: 16, color: primaryColor }} />
              <Typography variant="body2" fontWeight={600}>
                {data.Date ? moment(data.Date).format("DD MMMM YYYY") : "Date not set"}
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.3 }}>
              {isBirthday ? "Original Date (DOB)" : "Original Marriage Date (DOM)"}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              {isBirthday ? (
                <CakeIcon sx={{ fontSize: 16, color: primaryColor }} />
              ) : (
                <HeartIcon sx={{ fontSize: 16, color: primaryColor }} />
              )}
              <Typography variant="body2" fontWeight={600}>
                {data.OriginalDate ? moment(data.OriginalDate).format("DD MMMM YYYY") : "Not set"}
              </Typography>
            </Stack>
          </Box>

          {data.ID && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.3 }}>
                Reference ID
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {data.ID}
              </Typography>
            </Box>
          )}

          {data.MainMemberName && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.3 }}>
                Main Member / Prospect ID
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {data.MainMemberName}
              </Typography>
            </Box>
          )}

          {(data.Village || data.Tehsil || data.District || data.State) && (
            <Box sx={{ gridColumn: { xs: "1", sm: "span 2" } }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.3 }}>
                Location / Address
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <LocationIcon sx={{ fontSize: 16, color: primaryColor }} />
                <Typography variant="body2" fontWeight={600}>
                  {[
                    data.Village && `Village: ${data.Village}`,
                    data.Tehsil && `Tehsil: ${data.Tehsil}`,
                    data.District && `District: ${data.District}`,
                    data.State && `State: ${data.State}`,
                  ].filter(Boolean).join(" • ")}
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>

        {/* WhatsApp Wish Messenger Preview */}
        {data.Mobile && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <WhatsAppIcon sx={{ fontSize: 18, color: "#25D366" }} /> Send WhatsApp Greeting
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              value={customMsg}
              onChange={(e) => setCustomMsg(e.target.value)}
              placeholder="Type your greeting message..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.paper, 0.7),
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      {/* ── Action Buttons ── */}
      <DialogActions sx={{ p: 2.5, pt: 1, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
        <Button
          component="a"
          href={fullViewUrl}
          target="_blank"
          variant="outlined"
          size="small"
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Open Full Profile
        </Button>

        <Stack direction="row" spacing={1}>
          {data.Mobile && (
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={handleCall}
              startIcon={<PhoneIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              Call
            </Button>
          )}

          {data.Mobile && (
            <Button
              variant="contained"
              size="small"
              onClick={handleSendWhatsApp}
              startIcon={<WhatsAppIcon sx={{ fontSize: 17 }} />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#25D366",
                color: "#fff",
                "&:hover": { bgcolor: "#1EBE5D" },
                boxShadow: "0 4px 12px rgba(37, 211, 102, 0.3)",
              }}
            >
              Send WhatsApp
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
